import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Search, Trash2, Eye, Plus, ArrowUpDown } from 'lucide-react';
import { projectAnalysisApi, type AnalysisItem } from '../lib/api/projectAnalysis';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmModal from './common/ConfirmModal';
import { formatMoney } from '../lib/numberFormat';

const formatPrice = (n: string | number | null | undefined): string => {
  const num = typeof n === 'string' ? parseFloat(String(n).replace(/[,٬\s]/g, '')) : n;
  if (num == null || Number.isNaN(num) || num === 0) return '—';
  return formatMoney(num);
};

const estimateOf = (a: AnalysisItem): number =>
  parseFloat(String(a.initial_estimate ?? a.project?.initial_estimate ?? '').replace(/[,٬\s]/g, '')) || 0;

const totalOf = (a: AnalysisItem): number =>
  parseFloat(String(a.total_cost ?? '').replace(/[,٬\s]/g, '')) || 0;

const coefOf = (a: AnalysisItem): number | null => {
  const est = estimateOf(a);
  const total = totalOf(a);
  return est > 0 && total > 0 ? total / est : null;
};

type SortKey = 'name' | 'total' | 'coef' | 'updated';

const AnalysisList: React.FC = () => {
  const [analyses, setAnalyses] = useState<AnalysisItem[]>([]);
  const [pagination, setPagination] = useState({ total: 0, current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [deleteTarget, setDeleteTarget] = useState<AnalysisItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = (page = 1) => {
    setLoading(true);
    projectAnalysisApi
      .list({
        search: search || undefined,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        per_page: 20,
        page,
      })
      .then((res) => {
        setAnalyses(res.data);
        setPagination(res.pagination);
      })
      .catch(() => setAnalyses([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await projectAnalysisApi.destroy(deleteTarget.id);
      setDeleteTarget(null);
      load(pagination.current_page);
    } catch {
      setDeleting(false);
    } finally {
      setDeleting(false);
    }
  };

  const rows = useMemo(() => {
    const list = [...analyses];
    list.sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name, 'fa');
      if (sortKey === 'total') return totalOf(b) - totalOf(a);
      if (sortKey === 'coef') return (coefOf(b) ?? -1) - (coefOf(a) ?? -1);
      return String(b.updated_at ?? b.created_at ?? '').localeCompare(String(a.updated_at ?? a.created_at ?? ''));
    });
    return list;
  }, [analyses, sortKey]);

  return (
    <div className="flex flex-col h-full min-h-0 bg-white overflow-hidden border border-slate-200 rounded-xl shadow-sm" dir="rtl">
      <div className="p-3 lg:p-4 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center gap-3">
        <div>
          <h1 className="text-base font-black text-slate-900">آنالیزها</h1>
          <p className="text-xs text-slate-500">جستجو، مقایسه ضریب و ورود به کارگاه آنالیز</p>
        </div>
        <div className="flex flex-1 flex-col sm:flex-row items-stretch sm:items-center gap-2 lg:justify-end">
          <div className="relative min-w-0 sm:w-72">
            <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="نام آنالیز یا پروژه..."
              className="w-full pr-8 pl-3 py-2 border border-slate-200 rounded-lg bg-white text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          >
            <option value="all">همه وضعیت‌ها</option>
            <option value="active">فعال</option>
            <option value="inactive">غیرفعال</option>
          </select>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          >
            <option value="updated">جدیدترین</option>
            <option value="total">بیشترین بها</option>
            <option value="coef">بیشترین ضریب</option>
            <option value="name">نام</option>
          </select>
          <Link
            to="/analyses/create"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#0a4d29] text-white rounded-lg font-bold hover:bg-emerald-900 text-sm"
          >
            <Plus size={16} />
            آنالیز جدید
          </Link>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {loading && analyses.length === 0 ? (
          <LoadingSpinner variant="page" />
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-slate-500">
            <Calculator size={48} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">آنالیزی یافت نشد.</p>
            <Link to="/analyses/create" className="text-emerald-800 font-bold hover:underline mt-2 inline-block">
              ایجاد آنالیز جدید
            </Link>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[880px]">
            <thead className="sticky top-0 bg-slate-50 text-xs text-slate-500 z-10">
              <tr>
                <th className="p-3 text-right font-bold w-12">#</th>
                <th className="p-3 text-right font-bold">نام آنالیز</th>
                <th className="p-3 text-right font-bold">پروژه</th>
                <th className="p-3 text-right font-bold">برآورد</th>
                <th className="p-3 text-right font-bold">بهای کل</th>
                <th className="p-3 text-right font-bold">
                  <span className="inline-flex items-center gap-1">ضریب <ArrowUpDown size={12} /></span>
                </th>
                <th className="p-3 text-right font-bold">بخش‌ها</th>
                <th className="p-3 text-right font-bold">وضعیت</th>
                <th className="p-3 text-right font-bold w-28">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, idx) => {
                const est = estimateOf(a);
                const total = totalOf(a);
                const coef = coefOf(a);
                return (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-emerald-50/40">
                    <td className="p-3 font-mono text-slate-400">
                      {(pagination.current_page - 1) * 20 + idx + 1}
                    </td>
                    <td className="p-3">
                      <Link to={`/analyses/${a.id}`} className="font-bold text-slate-800 hover:text-emerald-800">
                        {a.name}
                      </Link>
                    </td>
                    <td className="p-3">
                      <span className="block truncate max-w-[220px]">{a.project?.name ?? '—'}</span>
                      {a.project?.contract_number && (
                        <span className="text-[11px] text-slate-400">({a.project.contract_number})</span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">{est > 0 ? formatPrice(est) : '—'}</td>
                    <td className="p-3 font-mono font-bold">{total > 0 ? formatPrice(total) : '—'}</td>
                    <td className="p-3 font-mono" dir="ltr">{coef != null ? coef.toFixed(4) : '—'}</td>
                    <td className="p-3 font-mono">{a.sections_count ?? 0}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${a.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                        {a.is_active ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/analyses/${a.id}`}
                          className="p-2 text-emerald-800 hover:bg-emerald-50 rounded-lg"
                          title="مشاهده"
                        >
                          <Eye size={16} />
                        </Link>
                        <button
                          onClick={() => setDeleteTarget(a)}
                          className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg"
                          title="حذف"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="p-3 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="text-xs font-bold text-slate-600">تعداد: {pagination.total}</div>
        {pagination.last_page > 1 && (
          <div className="flex gap-2 items-center">
            <button
              onClick={() => load(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-white disabled:opacity-50"
            >
              قبلی
            </button>
            <span className="px-2 text-xs text-slate-600">
              {pagination.current_page} / {pagination.last_page}
            </span>
            <button
              onClick={() => load(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.last_page}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs bg-white disabled:opacity-50"
            >
              بعدی
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        title="حذف آنالیز"
        message={deleteTarget ? `آیا از حذف آنالیز «${deleteTarget.name}» اطمینان دارید؟` : ''}
        confirmLabel="حذف"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
        danger
      />
    </div>
  );
};

export default AnalysisList;
